import { app } from '@microsoft/teams-js';
import { getGraphqlApiErrorReason } from '@ms/yammer-data/dist/api';
import { GraphqlErrorReason } from '@ms/yammer-data/dist/api/types';
import { AcquireTokenTimedoutError } from '@ms/yammer-data/dist/authentication';
import { getErrorMessage } from '@ms/yammer-data/dist/telemetry';
import { AcquireTokenAuthError } from '@ms/yammer-web-authenticators/dist/errors';

import { ExpectedFailureReason, FailedReason } from './error';
import { isInMetaOsHub } from './isInMetaOsHub';

const getFailedReason = (error?: unknown): FailedReason => {
  if (error instanceof AcquireTokenAuthError || error instanceof AcquireTokenTimedoutError) {
    return FailedReason.AuthFailed;
  }

  return FailedReason.Other;
};

/**
 * Notify the host app that our app has initialized.
 * This will trigger the Teams host to remove their loading spinner and unhide our iframe.
 */
type NotifyAppInitializationLoaded = () => Promise<void>;
export const notifyAppInitializationLoaded: NotifyAppInitializationLoaded = async () => {
  if (await isInMetaOsHub()) {
    app.notifyAppLoaded();
  }
};

/**
 * Notify the host app that the page's primary content has loaded and is ready for user interaction.
 * This triggers the Teams host to end the app load measurement, remove their loading spinner and unhide our iframe.
 */
type NotifyAppInitializationSuccess = () => Promise<void>;
export const notifyAppInitializationSuccess: NotifyAppInitializationSuccess = async () => {
  if (await isInMetaOsHub()) {
    app.notifySuccess();
  }
};

/**
 * Notify the host app that the app has failed to load.
 */
type NotifyAppInitializationFailure = (error?: unknown) => Promise<void>;
export const notifyAppInitializationFailure: NotifyAppInitializationFailure = async (error) => {
  if (!(await isInMetaOsHub())) {
    return;
  }

  const reason = getFailedReason(error);
  const message = getErrorMessage(error);
  const failedRequest = message ? { message, reason } : { reason };

  app.notifyFailure(failedRequest);
};

export type NotifyAppExpectedFailureReasons = [GraphqlErrorReason | GraphqlErrorReason[], ExpectedFailureReason];

interface NotifyAppExpectedFailure {
  (error?: unknown): Promise<void>;
  (error: unknown, expectedReasons: NotifyAppExpectedFailureReasons): Promise<void>;
}
export const notifyAppExpectedFailure: NotifyAppExpectedFailure = async (
  error?: unknown,
  expectedReasons?: NotifyAppExpectedFailureReasons
) => {
  if (!(await isInMetaOsHub())) {
    return;
  }

  let expectedFailureReason = ExpectedFailureReason.Other;

  if (expectedReasons) {
    const [graphqlReasons, metaOsReason] = expectedReasons;
    const errorReason = getGraphqlApiErrorReason(error);

    const hasGraphReasonMatch = (Array.isArray(graphqlReasons) ? graphqlReasons : [graphqlReasons]).some(
      (reason) => reason === errorReason
    );

    if (hasGraphReasonMatch) {
      expectedFailureReason = metaOsReason;
    }
  }

  const message = getErrorMessage(error);
  const expectedFailure = message ? { message, reason: expectedFailureReason } : { reason: expectedFailureReason };

  app.notifyExpectedFailure(expectedFailure);
};
